/**
 * IntakeFileHistoryTable
 *
 * ┌──────────────────────────────────────────────────────────────────────────────┐
 * │ NOTE: This component lives in @adc/ui-core despite being intake-domain-     │
 * │ specific. This is intentional: it must be consumed by both                   │
 * │ @adc/ui-morpheus (resi-fe-btbhome / Morpheus) and @adc/ui-adc              │
 * │ (resi-fe-attorney-dashboard / AFI). ui-core is the only shared layer        │
 * │ between these two design-system branches.                                    │
 * │                                                                              │
 * │ ui-core has historically housed generic, business-logic-free components.     │
 * │ This component is an exception made to avoid code duplication across two     │
 * │ products that share identical file history presentation needs.               │
 * │ The business logic (data fetching, auth checks) remains in @adc/blueprint   │
 * │ hooks — this component is purely presentational.                             │
 * │                                                                              │
 * │ Ticket: IGN-97                                                               │
 * └──────────────────────────────────────────────────────────────────────────────┘
 */
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Table, Dimmer, Loader } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import type { IntakeBatch } from '@adc/sdk/types'
import Text from '../elementals/text'
import Icon from '../elementals/icon'
import InfoPill from '../elementals/infoPill'
import Statistic from '../elementals/statistic'
import FilterToolbar from '../elementals/filterToolbar'
import PopupTooltip from '../overlays/popupTooltip'
import useOffsetPagination from '../../hooks/useOffsetPagination'
import type { PaginationState } from '../../hooks/useOffsetPagination'
import { ColorNames } from '../../constants/colorVars'
import {
  IntakeFileHistoryColumn,
  ALL_COLUMNS,
  type IntakeColumnOverride,
  type IntakeFileHistoryTableProps,
} from './types'
import { IssueDetails } from './IssueDetails'
import { getMappingPillColors } from './mappingStatusColors'
// @ts-ignore no types for scss modules
import styles from './styles.scss'
import DownloadLink from '../downloadLink'
import { Button } from '../elementals'

const cx = classnames.bind(styles)

// ─── Formatters ─────────────────────────────────────────────────────────────────

const TableHeader = ({
  elmId,
  onRefresh,
}: {
  elmId: string
  onRefresh: (() => void) | undefined
}) => {
  return (
    <div className={cx('intake-file-history__header')}>
      <Text weight='medium' size='small'>
        File History
      </Text>
      {onRefresh && (
        <PopupTooltip
          on='hover'
          position='top center'
          offset={0}
          horizontalOffset={undefined}
          verticalOffset={undefined}
          trigger={
            <Button
              type='button'
              role='tertiary'
              onClick={onRefresh}
              collapse
              elmId={`${elmId}-refresh`}
              aria-label='Refresh file history'
            >
              <Icon
                name='arrows-rotate'
                iconStyle='solid'
                size='medium'
                color='gray'
                style={{ margin: 'auto' }}
              />
            </Button>
          }
          content='Refresh data'
        />
      )}
    </div>
  )
}
const formatDate = (
  dateStr: string | number | Date | null | undefined
): string => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString()
}

const formatStatus = (status: string | null | undefined): string => {
  if (!status) return 'UNKNOWN'
  return status.replace(/_/g, ' ')
}

const getStatusPillColors = (
  status: string | null | undefined
): { bg: ColorNames; fg: ColorNames; style?: React.CSSProperties } => {
  switch (status) {
    case 'COMPLETED':
      return { bg: ColorNames.SuccessExtraLight, fg: ColorNames.SuccessDark }
    case 'IN_PROGRESS':
    case 'NEW':
    case 'PENDING_UPLOAD':
      // Yellow tones matching Morpheus (no DS yellow palette exists)
      return {
        bg: ColorNames.OffWhiteDark,
        fg: ColorNames.GrayDark,
        style: {
          background: '#fff8e1',
          color: '#f57f17',
          border: '1px solid #fff8e1',
        },
      }
    case 'FAILED':
    case 'COMPLETED_WITH_FAILED':
      return { bg: ColorNames.ErrorExtraLight, fg: ColorNames.ErrorDark }
    default:
      return { bg: ColorNames.GrayExtraLight, fg: ColorNames.GrayDark }
  }
}

const formatCostScore = (score: number | null | undefined): React.ReactNode => {
  if (score === null || score === undefined) return '—'
  const color = score <= 0.3 ? 'green' : score <= 0.6 ? 'orange' : 'red'
  return <span style={{ color, fontWeight: 500 }}>{score.toFixed(2)}</span>
}

// ─── Column Definitions ─────────────────────────────────────────────────────────

interface ColumnDef {
  key: IntakeFileHistoryColumn
  header: string
  tooltip?: string
  render: (batch: IntakeBatch, index: number) => React.ReactNode
}

const COLUMN_DEFS: ColumnDef[] = [
  {
    key: IntakeFileHistoryColumn.Organization,
    header: 'Organization',
    tooltip:
      'The organization this file belongs to. Shown when the table aggregates file history across organizations. Falls back to the organization id when the name is unavailable.',
    render: batch => batch.source?.name || batch.source?.organization_id || '—',
  },
  {
    key: IntakeFileHistoryColumn.BatchId,
    header: 'Batch ID',
    tooltip:
      'Unique identifier for this file. One batch = one uploaded file. Always present.',
    render: batch => batch.intake_batch_id || '—',
  },
  {
    key: IntakeFileHistoryColumn.IngestionId,
    header: 'Ingestion ID',
    tooltip:
      'Groups batches from a single upload — a large file can split into multiple batches, so this ID may repeat across rows. Empty for files not routed through the upload pipeline.',
    render: batch => batch.ingestion_id || '—',
  },
  {
    key: IntakeFileHistoryColumn.FileName,
    header: 'File Name',
    render: batch => batch.file_name || '—',
  },
  {
    key: IntakeFileHistoryColumn.Classification,
    header: 'Classification',
    render: batch =>
      batch.classification_type
        ? batch.classification_type.replace(/_/g, ' ')
        : '—',
  },
  {
    key: IntakeFileHistoryColumn.UploadDate,
    header: 'Upload Date',
    render: batch => formatDate(batch.upload_date_time),
  },
  {
    key: IntakeFileHistoryColumn.UploadedBy,
    header: 'Uploaded By',
    render: batch => {
      const user = batch.created_by_user
      if (user?.email) return user.email
      return batch.created_by || '\u2014'
    },
  },
  {
    key: IntakeFileHistoryColumn.Status,
    header: 'Status',
    render: batch => {
      const { bg, fg, style } = getStatusPillColors(batch.status)
      return (
        <InfoPill
          content={formatStatus(batch.status)}
          backgroundColor={bg}
          foregroundColor={fg}
          size='x-small'
          style={style}
          elmId={`status-pill-${batch.intake_batch_id}`}
        />
      )
    },
  },
  {
    key: IntakeFileHistoryColumn.Records,
    header: 'Records',
    render: batch => batch.record_count ?? '—',
  },
  {
    key: IntakeFileHistoryColumn.Success,
    header: 'Success',
    render: batch => batch.success_count ?? '—',
  },
  {
    key: IntakeFileHistoryColumn.Errors,
    header: 'Errors',
    render: batch => batch.error_count ?? '—',
  },
  {
    key: IntakeFileHistoryColumn.CoverageScore,
    header: 'Coverage Score',
    render: batch => formatCostScore(batch.coverage_cost_score),
  },
  {
    key: IntakeFileHistoryColumn.StructuralScore,
    header: 'Structural Score',
    render: batch => formatCostScore(batch.structural_cost_score),
  },
  {
    key: IntakeFileHistoryColumn.Mapping,
    header: 'Mapping',
    tooltip: 'Organization mapping configuration used to process this file.',
    render: batch => {
      const mapping = batch.mapping
      if (mapping?.name) {
        const colors = getMappingPillColors(mapping.status)
        return (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {mapping.version != null && (
              <span title={mapping.status || undefined}>
                <InfoPill
                  content={`v${mapping.version}`}
                  backgroundColor={colors.bg}
                  foregroundColor={colors.fg}
                  size='x-small'
                  elmId={`mapping-pill-${batch.intake_batch_id}`}
                />
              </span>
            )}
            <span>{mapping.name}</span>
          </span>
        )
      }
      return batch.organization_mapping_id ?? '\u2014'
    },
  },
]

// ─── Component ──────────────────────────────────────────────────────────────────

// ─── Expandable Row Detail ──────────────────────────────────────────────────────

const ExpandedRowDetail: React.FC<{
  batch: IntakeBatch
  colSpan: number
  elmId: string
}> = ({ batch, colSpan, elmId }) => {
  const results = batch.results || []
  const outputFiles = batch.output_files || []

  const getCountForType = (type: string): number | null => {
    const result = results.find(r => r?.result_type === type)
    return result?.row_count ?? null
  }

  return (
    <Table.Row data-elm-id={`${elmId}-expanded`}>
      <Table.Cell colSpan={colSpan} className={cx('expanded-row')}>
        <div className={cx('expanded-row__content')}>
          {/* Summary stats */}
          <div className={cx('expanded-row__summary')}>
            <Statistic
              label='Total Records'
              value={batch.record_count ?? null}
              datatype='count'
              accentColor='gray'
              elmId={`${elmId}-stat-total`}
            />
            <Statistic
              label='Success'
              value={getCountForType('SUCCESS') ?? batch.success_count ?? null}
              datatype='count'
              accentColor='success'
              elmId={`${elmId}-stat-success`}
            />
            <Statistic
              label='Errors'
              value={getCountForType('ERROR') ?? batch.error_count ?? null}
              datatype='count'
              accentColor='error'
              elmId={`${elmId}-stat-errors`}
            />
            {getCountForType('WARNING') != null && (
              <Statistic
                label='Warnings'
                value={getCountForType('WARNING')}
                datatype='count'
                accentColor='caution'
                elmId={`${elmId}-stat-warnings`}
              />
            )}
            {getCountForType('ROUTED') != null && (
              <Statistic
                label='Routed'
                value={getCountForType('ROUTED')}
                datatype='count'
                accentColor='info'
                elmId={`${elmId}-stat-routed`}
              />
            )}
            {getCountForType('REJECTED') != null && (
              <Statistic
                label='Rejected'
                value={getCountForType('REJECTED')}
                datatype='count'
                accentColor='error'
                elmId={`${elmId}-stat-rejected`}
              />
            )}
            {getCountForType('FLAGGED') != null && (
              <Statistic
                label='Flagged'
                value={getCountForType('FLAGGED')}
                datatype='count'
                accentColor='caution'
                elmId={`${elmId}-stat-flagged`}
              />
            )}
          </div>
          {/* Issue Details — grouped accordion view */}
          <IssueDetails results={results} elmId={`${elmId}-issue-details`} />

          <div className={cx('file-download-section')}>
            {/* Output files */}
            {outputFiles.length > 0 && (
              <div className={cx('expanded-row__output')}>
                <Text size='small' weight='medium' color='gray'>
                  Output Files
                </Text>
                <div className={cx('expanded-row__files')}>
                  {outputFiles.map((file, idx) => (
                    <span
                      key={file?.id || file?.file_name || idx}
                      onClick={e => e.stopPropagation()}
                    >
                      {file && (
                        <DownloadLink resource={file}>
                          {file?.file_name || 'Download'}
                        </DownloadLink>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Original uploaded file */}
            {batch.intake_batch_id && (
              <div className={cx('expanded-row__output')}>
                <Text size='small' weight='medium' color='gray'>
                  Original File
                </Text>
                <div className={cx('expanded-row__files')}>
                  <span onClick={e => e.stopPropagation()}>
                    <DownloadLink resource={batch}>
                      {batch.file_name || 'Original File'}
                    </DownloadLink>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* No detail data fallback */}
          {results.length === 0 && outputFiles.length === 0 && (
            <Text size='mini' color='gray'>
              No detail data available for this batch.
            </Text>
          )}
        </div>
      </Table.Cell>
    </Table.Row>
  )
}

// ─── Component ──────────────────────────────────────────────────────────────────

export const IntakeFileHistoryTable: React.FC<IntakeFileHistoryTableProps> = ({
  rows,
  isLoading,
  totalCount,
  currentPage,
  pageSize = 10,
  onPageChange,
  visibleColumns = ALL_COLUMNS,
  searchText = '',
  onSearchTextChange,
  searchPlaceholder = 'Search by file name (min 3 chars)',
  filterData,
  filterActions,
  expandable = false,
  onRefresh,
  onReprocess,
  reprocessingBatchId,
  elmId = 'intake-file-history',
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  // `visibleColumns` entries are either a bare enum (default rendering) or an
  // override object. Split into the set of visible keys (drives which
  // COLUMN_DEFS show, in canonical order) and a per-column render override map.
  const { visibleKeys, overrideRenderByKey } = React.useMemo(() => {
    const keys = new Set<IntakeFileHistoryColumn>()
    const overrides = new Map<
      IntakeFileHistoryColumn,
      IntakeColumnOverride['render']
    >()
    visibleColumns.forEach(entry => {
      if (typeof entry === 'string') {
        keys.add(entry)
      } else {
        keys.add(entry.column)
        overrides.set(entry.column, entry.render)
      }
    })
    return { visibleKeys: keys, overrideRenderByKey: overrides }
  }, [visibleColumns])

  // Reset expanded state when data changes (page navigation or refresh)
  useEffect(() => {
    setExpandedRows(new Set())
  }, [rows, currentPage])

  const toggleRow = useCallback((batchId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(batchId)) {
        next.delete(batchId)
      } else {
        next.add(batchId)
      }
      return next
    })
  }, [])

  const activeColumns = React.useMemo(() => {
    // The Actions column lives here (not in the static COLUMN_DEFS) because its
    // cell closes over the onReprocess / reprocessingBatchId props. It only does
    // anything when a consumer wires onReprocess.
    const actionsColumnDef: ColumnDef = {
      key: IntakeFileHistoryColumn.Actions,
      header: 'Actions',
      render: batch => {
        if (!onReprocess || !batch.is_reprocessable) return '\u2014'
        const batchId = batch.intake_batch_id
        const isReprocessing =
          batchId != null && String(batchId) === String(reprocessingBatchId)
        return (
          <span className={cx('center-icon-column')}>
            <Button
              type='button'
              role='tertiary'
              size='small'
              collapse
              onClick={(e: React.MouseEvent) => {
                // Rows are click-to-expand; keep the action from toggling the row.
                e.stopPropagation()
                onReprocess(batch)
              }}
              loading={isReprocessing}
              disabled={isReprocessing}
              elmId={`${elmId}-reprocess-${batchId}`}
              aria-label='Reprocess batch'
            >
              <Icon
                name='arrow-rotate-right'
                iconStyle='solid'
                size='medium'
                color='gray'
                title='Reprocess this file — re-runs the mapping engine on its failed rows.'
                style={{ margin: 'auto' }}
              />
            </Button>
          </span>
        )
      },
    }
    return [...COLUMN_DEFS, actionsColumnDef].filter(col =>
      visibleKeys.has(col.key)
    )
  }, [visibleKeys, onReprocess, reprocessingBatchId, elmId])

  const totalColSpan = activeColumns.length + (expandable ? 1 : 0)

  const searchTextRef = useRef(searchText)
  searchTextRef.current = searchText
  const setSearchTextRef = useRef(onSearchTextChange)
  setSearchTextRef.current = onSearchTextChange

  const hasMorePages = totalCount > pageSize

  const { Paginator } = useOffsetPagination({
    totalCount: totalCount || 0,
    pageSize,
    initialOffset: (currentPage - 1) * pageSize,
    onPageChange: (newPagination: PaginationState) => {
      if (newPagination.currentPage !== currentPage) {
        onPageChange(newPagination.currentPage)
      }
    },
  })

  // ─── Empty state (no rows, no active filters) ──────────────────────────────

  const hasActiveFilters = Boolean(
    searchText ||
      (filterData?.filterState &&
        Object.values(filterData.filterState).some(v => v != null))
  )

  if (!isLoading && rows.length === 0 && !hasActiveFilters) {
    return (
      <div className={cx('intake-file-history')} data-elm-id={elmId}>
        <TableHeader elmId={elmId} onRefresh={onRefresh} />
        <div
          className={cx('intake-file-history__empty')}
          data-elm-id={`${elmId}-empty`}
        >
          <Text>No files have been uploaded for this organization yet.</Text>
        </div>
      </div>
    )
  }

  // ─── Toolbar ──────────────────────────────────────────────────────────────

  const renderToolbar = () => {
    if (!onSearchTextChange && !filterData) return null
    return (
      <form
        onSubmit={e => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <FilterToolbar
          searchText={searchTextRef.current}
          setSearchText={setSearchTextRef.current}
          searchPlaceholder={searchPlaceholder}
          filterData={filterData}
          filterActions={filterActions}
        />
      </form>
    )
  }

  return (
    <div className={cx('intake-file-history')} data-elm-id={elmId}>
      <TableHeader elmId={elmId} onRefresh={onRefresh} />
      {renderToolbar()}
      <Dimmer.Dimmable dimmed blurring={isLoading}>
        <Dimmer inverted active={isLoading}>
          <Loader active={isLoading} />
        </Dimmer>
        {rows.length === 0 && !isLoading ? (
          <div className={cx('intake-file-history__empty')}>
            <Text>No results match the current filters.</Text>
          </div>
        ) : (
          <>
            <div className={cx('intake-file-history__scroll-wrapper')}>
              <Table celled compact data-elm-id={`${elmId}-table`}>
                <Table.Header>
                  <Table.Row>
                    {expandable && (
                      <Table.HeaderCell className={cx('expand-col')} />
                    )}
                    {activeColumns.map(col => (
                      <Table.HeaderCell key={col.key}>
                        {col.header}
                        {col.tooltip && (
                          <>
                            {' '}
                            <PopupTooltip
                              on='hover'
                              position='top center'
                              offset={0}
                              horizontalOffset={undefined}
                              verticalOffset={undefined}
                              trigger={
                                <Icon
                                  name='circle-info'
                                  iconStyle='regular'
                                  size='small'
                                  color='gray'
                                />
                              }
                              content={col.tooltip}
                            />
                          </>
                        )}
                      </Table.HeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {rows.map((batch, index) => {
                    const batchId = `row-${currentPage}-${index}`
                    const isExpanded = expandable && expandedRows.has(batchId)
                    return (
                      <React.Fragment key={batchId}>
                        <Table.Row
                          data-elm-id={`${elmId}-row-${index}`}
                          className={cx({
                            'row--expandable': expandable,
                            'row--expanded': isExpanded,
                          })}
                          onClick={
                            expandable ? () => toggleRow(batchId) : undefined
                          }
                        >
                          {expandable && (
                            <Table.Cell className={cx('expand-col')}>
                              <Icon
                                elmId={`${elmId}-row-${index}-toggle`}
                                name={
                                  isExpanded ? 'chevron-down' : 'chevron-right'
                                }
                                iconStyle='solid'
                                size='small'
                                color='gray'
                              />
                            </Table.Cell>
                          )}
                          {activeColumns.map(col => {
                            const renderCell =
                              overrideRenderByKey.get(col.key) ?? col.render
                            return (
                              <Table.Cell
                                key={col.key}
                                data-elm-id={`${elmId}-${col.key}-${index}`}
                              >
                                {renderCell(batch, index)}
                              </Table.Cell>
                            )
                          })}
                        </Table.Row>
                        {isExpanded && (
                          <ExpandedRowDetail
                            batch={batch}
                            colSpan={totalColSpan}
                            elmId={`${elmId}-row-${index}`}
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                </Table.Body>
              </Table>
            </div>
            {hasMorePages && (
              <div className={cx('intake-file-history__paginator')}>
                {Paginator}
              </div>
            )}
          </>
        )}
      </Dimmer.Dimmable>
    </div>
  )
}

export default IntakeFileHistoryTable
